import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Input, 
  Modal, 
  Form, 
  Spin, 
  Table,
  Space,
  Tag,
  message,
  Popconfirm,
  Empty,
  Typography,
  ConfigProvider,
  Pagination,
  Switch,
  Select
} from 'antd';
import { 
  FaRegTrashAlt, 
  FaEdit, 
  FaSearch, 
  FaPlus,
  FaUser,
  FaLock,
  FaEnvelope,
  FaUserShield
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';

const { Title } = Typography;
const { Option } = Select;

const GerenciarUsuarios = () => {
  // Estados principais
  const [usuarios, setUsuarios] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  
  // Estados para modais
  const [isModalVisible, setIsModalVisible] = useState(false);
  
  // Estados para seleção
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  
  // Formulário
  const [form] = Form.useForm();

  // Cores do degradê
  const primaryColor = 'rgb(249, 130, 68)';
  const secondaryColor = 'rgb(247, 72, 115)';
  const gradient = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;

  // Cores para os perfis (baseado na descrição)
  const getPerfilColorByDescription = (descricao) => {
    const desc = descricao?.toLowerCase();
    if (desc?.includes('administrador')) return 'red';
    if (desc?.includes('gestor')) return 'blue';
    if (desc?.includes('vendas')) return 'green';
    if (desc?.includes('caixa')) return 'orange';
    return 'default';
  };

  // Buscar dados iniciais
  useEffect(() => {
    fetchData();
  }, []);

        useEffect(() => {
       document.title = "Usuários";
     
    }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Buscar usuários e perfis em paralelo
      const [usuariosData, perfisData] = await Promise.all([
        Api.ListUser(),
        Api.ListPerfil()
      ]);
      
      setUsuarios(usuariosData.map(u => ({ 
        ...u,
        key: u.id,
        ativo: u.status === 'Ativo'
      })));
      
      setPerfis(perfisData || []);
    } catch (error) {
      toast.error('Erro ao carregar dados: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Função para obter cor do perfil
  const getPerfilColor = (perfilNome) => {
    if (!perfilNome) return 'default';
    return getPerfilColorByDescription(perfilNome);
  };

  // Função para obter label do perfil
  const getPerfilLabel = (perfilNome) => {
    return perfilNome || 'Sem perfil';
  };
     
  // Filtrar usuários
  const filteredUsuarios = usuarios.filter(usuario => {
    const perfilNome = usuario.perfil || '';
    return (
      (usuario.nome || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (usuario.email || '').toLowerCase().includes(searchText.toLowerCase()) ||
      perfilNome.toLowerCase().includes(searchText.toLowerCase())
    );
  });

  // Paginação
  const paginatedUsuarios = filteredUsuarios.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Colunas da tabela
  const columns = [
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
      sorter: (a, b) => a.nome.localeCompare(b.nome),
      render: (text, record) => (
        <Space>
          <FaUser style={{ color: primaryColor }} />
          <span>{text}</span>
        </Space>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => (
        <Space>
          <FaEnvelope style={{ color: '#1890ff' }} />
          <span>{email}</span>
        </Space>
      )
    },
    {
      title: 'Perfil',
      dataIndex: 'perfil',
      key: 'perfil',
      render: (perfil) => (
        <Tag 
          color={getPerfilColor(perfil)} 
          icon={<FaUserShield />}
          style={{ display: 'flex', alignItems: 'center', width: 'fit-content', gap: 4 }}
        >
          {getPerfilLabel(perfil)}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'Ativo' ? 'green' : 'red'}>
          {status}
        </Tag>
      )
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            icon={<FaEdit />}
            onClick={() => {
              setSelectedUsuario(record);
              form.setFieldsValue({
                nome: record.nome,
                email: record.email,
                perfil_id: record.id_perfil,
                ativo: record.status === 'Ativo',
              });
              setIsModalVisible(true);
            }}
            style={{
              color: primaryColor,
              borderColor: primaryColor,
              borderRadius: 6
            }}
          />
          
          <Popconfirm
            title="Excluir este usuário?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
            okButtonProps={{
              danger: true,
              style: { borderRadius: 6 }
            }}
            cancelButtonProps={{
              style: { borderRadius: 6 }
            }}
          >
            <Button 
              icon={<FaRegTrashAlt />}
              danger
              style={{ borderRadius: 6 }}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Manipuladores CRUD  
  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const userData = {
        nome: values.nome,
        email: values.email,
        id_perfil: values.perfil_id,
        status: values.ativo ? 'Ativo' : 'Inativo'
      };

      if (values.password) {
        userData.password = values.password;
      }

      if (selectedUsuario) {
        userData.id = selectedUsuario.id;
        const response = await Api.AlterUser(userData);
        
        if(response.success){
          fetchData();
          toast.success('Usuário atualizado com sucesso!');
        } else if(response.message === 'Request failed with status code 409'){
          toast.error('Já existe um usuário com este email.');
        }
        else if(response.message === 'Request failed with status code 402'){
          toast.error('Não pode editar esse usuário');
        }
        else {
          toast.error(response.message);
        }
      } else {
        if (!values.password) {
          toast.error('A senha é obrigatória para novo usuário!');
          return;
        }
        
        const response = await Api.AddUser(userData);
        
        if(response.success){
          fetchData();
          toast.success('Usuário adicionado com sucesso!');
        } else if(response.message === 'Request failed with status code 409'){
          toast.error('Já existe um usuário com este email.');
        } else {
          toast.error(response.message);
        }
      }

      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      toast.error('Erro ao salvar usuário: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await Api.DeleteUser({ id });

      if(response.success){
        fetchData();
        toast.success('Usuário excluído com sucesso!');
      } else if(response.message === 'Request failed with status code 409'){
        fetchData();
        toast.error('Este usuário está vinculado a registros e não pode ser excluído.');
      } 
      else if(response.message === 'Request failed with status code 402'){
        toast.error('Não pode eliminar esse usuário');
      }
      else {
        toast.error(response.message);
      }
      
      setCurrentPage(1);
    } catch (error) {
      toast.error('Erro ao excluir usuário: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: primaryColor,
          borderRadius: 8,
        },
      }}
    >
      <div style={{ 
        padding: 24,
        background: '#f8f9fa',
        minHeight: '100vh'
      }}>
        <ToastContainer position="top-right" />
        
        {/* Cabeçalho */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: 24,
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16
        }}>
          <Title level={2} style={{ 
            margin: 0,
            background: gradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block'
          }}>
            USUÁRIOS
          </Title>
          
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Input
              placeholder="Pesquisar usuários..."
              prefix={<FaSearch style={{ color: 'rgba(0,0,0,0.3)' }} />}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              style={{ 
                width: 250,
                borderRadius: 8,
                border: '1px solid rgba(0,0,0,0.1)'
              }}
            />
            
            <Button 
              type="primary"
              icon={<FaPlus />}
              onClick={() => {
                setSelectedUsuario(null);
                form.resetFields();
                setIsModalVisible(true);
              }}
              style={{ 
                background: gradient,
                border: 'none',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(247, 72, 115, 0.3)',
                height: 40,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              Novo Usuário
            </Button>
          </div>
        </div>

        {/* Tabela de Usuários */}
        <Spin spinning={loading}>
          {filteredUsuarios.length > 0 ? (
            <>
              <Table
                columns={columns}
                dataSource={paginatedUsuarios}
                pagination={false}
                scroll={{ x: 1000 }}
                style={{
                  borderRadius: 8,
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
                locale={{
                  emptyText: 'Nenhum usuário encontrado'
                }}
              />
              
              {/* Paginação */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginTop: 32,
                padding: '16px 0'
              }}>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={filteredUsuarios.length}
                  onChange={(page) => setCurrentPage(page)}
                  showSizeChanger={false}
                  style={{
                    borderRadius: 8,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
            </>
          ) : (
            <Empty
              description={
                <span style={{ color: 'rgba(0,0,0,0.5)' }}>
                  Nenhum usuário encontrado {searchText ? 'com o filtro atual' : ''}
                </span>
              }
              style={{ 
                marginTop: 50,
                padding: 40,
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}
            />
          )}
        </Spin>

        {/* Modal de edição/criação */}
        <Modal
          forceRender
          title={
            <span style={{
              background: gradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block'
            }}>
              {selectedUsuario ? 'Editar Usuário' : 'Novo Usuário'}
            </span>
          }
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
          }}
          footer={[
            <Button 
              key="cancel" 
              onClick={() => {
                setIsModalVisible(false);
              }}
              style={{ borderRadius: 6 }}
            >
              Cancelar
            </Button>,
            <Button 
              key="submit" 
              type="primary" 
              loading={loading}
              onClick={() => form.submit()}
              style={{ 
                background: gradient,
                border: 'none',
                borderRadius: 6,
                boxShadow: '0 2px 8px rgba(247, 72, 115, 0.3)'
              }}
            >
              {selectedUsuario ? 'Atualizar' : 'Salvar'}
            </Button>
          ]}
          width={600}
          destroyOnClose
          styles={{
            header: {
              borderBottom: '1px solid rgba(0,0,0,0.05)'
            },
            footer: {
              borderTop: '1px solid rgba(0,0,0,0.05)'
            }
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ ativo: true }}
          >
            <Form.Item
              name="nome"
              label="Nome Completo"
              rules={[{ required: true, message: 'Campo obrigatório' }]}
            >
              <Input 
                placeholder="Digite o nome completo" 
                maxLength={100}
                style={{ borderRadius: 6 }}
              />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Campo obrigatório' },
                { type: 'email', message: 'Email inválido' }
              ]}
            >
              <Input 
                type="email"
                placeholder="Digite o email"
                style={{ borderRadius: 6 }}
              />
            </Form.Item>

            <Form.Item
              name="perfil_id"
              label="Perfil de Acesso"
              rules={[{ required: true, message: 'Selecione um perfil' }]}
            >
              <Select
                placeholder="Selecione o perfil do usuário"
                style={{ borderRadius: 6 }}
                suffixIcon={<FaUserShield style={{ color: primaryColor }} />}
              >
                {perfis.map(perfil => (
                  <Option key={perfil.id} value={perfil.id}>
                    <Space>
                      <Tag color={getPerfilColorByDescription(perfil.descricao)} style={{ margin: 0 }}>
                        {perfil.descricao}
                      </Tag>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="password"
              label="Senha"
              rules={[
                { 
                  required: !selectedUsuario, 
                  message: 'Campo obrigatório para novo usuário' 
                },
                { 
                  min: 6, 
                  message: 'A senha deve ter pelo menos 6 caracteres' 
                }
              ]}
            >
              <Input.Password
                placeholder={selectedUsuario ? "Deixe em branco para manter a senha atual" : "Digite a senha"}
                style={{ borderRadius: 6 }}
              />
            </Form.Item>

            <Form.Item
              name="ativo"
              label="Status"
              valuePropName="checked"
            >
              <Switch 
                checkedChildren="Ativo" 
                unCheckedChildren="Inativo" 
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default GerenciarUsuarios;